import { useState, useEffect, useCallback, useRef } from 'react'
import { getOrders, getOrderById } from '../services/orderService'
import { ShippingOrder } from '../DeShippingTableWrapper'
import { SortOrder, FilterMatchMode } from 'primereact/api'

// Initial filters structure
const initialFilters = {
  global: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
  order_id: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
  order_name: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
  fulfillment_status: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
  product_sku: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
  product_name: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
  fulfillment_location: {operator: 'and', constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
}

export function useOrderData() {
  const [orders, setOrders] = useState<ShippingOrder[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({ first: 0, rows: 25, totalRecords: 0 })
  const [sort, setSort] = useState<{ field?: string, order?: SortOrder }>({})
  const [filters, setFilters] = useState<any>(initialFilters)
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getOrders()
      
      setOrders(data)
      setPagination(prev => ({ ...prev, totalRecords: data.length }))
    } catch (err) {
      console.error('Error loading orders:', err)
      setError(err instanceof Error ? err.message : 'Failed to load orders')
    } finally {
      setLoading(false)
    }
  }, [])

  const silentRefresh = useCallback(async () => {
    try {
      const data = await getOrders()
      setOrders(data)
      setPagination(prev => ({ ...prev, totalRecords: data.length }))
    } catch (err) {
      console.error('Silent refresh error:', err)
      // Don't update error state - keep it silent
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Set up auto-refresh every 30 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      silentRefresh()
    }, 30000) // 30 seconds

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [silentRefresh])

  const setPage = useCallback((page: { first: number, rows: number }) => {
    setPagination(prev => ({ ...prev, first: page.first, rows: page.rows }))
  }, [])

  const setSortState = useCallback((sortField?: string, sortOrder?: SortOrder) => {
    setSort({ field: sortField, order: sortOrder })
  }, [])
  
  const setFiltersState = useCallback((newFilters: any) => {
    setFilters(newFilters)
    setPagination(prev => ({ ...prev, first: 0 })) // Reset to first page when filtering
  }, [])

  const refresh = useCallback(() => {
    loadData()
  }, [loadData])

  // New method to update a single order
  const updateOrder = useCallback((updatedOrder: ShippingOrder) => {
    setOrders(currentOrders => 
      currentOrders.map(order => 
        order.order_id === updatedOrder.order_id ? updatedOrder : order
      )
    )
  }, [])

  const refreshSingleOrder = useCallback(async (orderId: string) => {
    try {
      const updatedOrder = await getOrderById(orderId)
      updateOrder(updatedOrder)
      return updatedOrder
    } catch (err) {
      console.error(`Error refreshing order ${orderId}:`, err)
      setError(err instanceof Error ? err.message : `Failed to refresh order ${orderId}`)
      throw err
    }
  }, [updateOrder])

  return {
    orders,
    loading,
    error,
    pagination,
    sort,
    filters,
    setPage,
    setSort: setSortState,
    setFilters: setFiltersState,
    refresh,
    resetFilters: () => setFilters(initialFilters),
    updateOrder,
    refreshSingleOrder
  }
}